import { Link, useParams } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { cn } from '@/lib/cn';
import { SECTIONS, GROUPS, findSection } from './sections';
import { useMyPermissions } from '@/lib/permissions';
import { useUI } from '@/store/ui';

/**
 * Settings shell — sidebar nav driven by the section registry, with the
 * active section rendered on the right. Each section is its own component
 * (see `./sections.tsx`).
 */
export function SettingsLayout() {
  const { section: sectionKey } = useParams({ strict: false }) as { section?: string };
  const active = findSection(sectionKey);
  const Section = active.component;
  const perms = useMyPermissions();
  const expandedGroup = useUI((s) => s.settingsExpandedGroup);
  const setExpandedGroup = useUI((s) => s.setSettingsExpandedGroup);
  // Single-open accordion: one group open at a time. Until the user picks one,
  // default to the active section's group so the current page is visible.
  const effectiveExpanded = expandedGroup === undefined ? active.group : expandedGroup;
  // Filter sections by requireSystem. A non-system user landing on a
  // requireSystem section sees the 403 message from the underlying handler
  // anyway, but the nav hides the entry so they don't trip into it.
  const visibleSections = perms.isSystem
    ? SECTIONS
    : SECTIONS.filter((s) => !s.requireSystem);

  return (
    <>
      <PageHeader
        crumbs={[{ label: 'Settings', to: '/settings' }, { label: active.label }]}
        title={active.label}
        subtitle={active.description}
      />

      <div className="flex-1 px-4 sm:px-6 lg:px-8 pt-6 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-[264px_1fr] gap-8 lg:gap-10">
          {/* Sidebar nav — grouped, each group collapsible. Wrapped in its own
           *  hairline-bordered card so the "menu within the page" reads as a
           *  deliberate settings rail rather than loose floating links. */}
          <nav className="space-y-3 surface-card p-2 lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto">
            {GROUPS.map((g) => {
              const items = visibleSections.filter((s) => s.group === g.key);
              if (items.length === 0) return null;
              // Opening a different group collapses whichever was open; clicking
              // the open group again collapses it (null).
              const open = g.key === effectiveExpanded;
              return (
                <div key={g.key}>
                  <button
                    type="button"
                    onClick={() => setExpandedGroup(open ? null : g.key)}
                    aria-expanded={open}
                    className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-micro-uppercase text-stone hover:text-ink hover:bg-surface transition-colors"
                  >
                    <ChevronDown
                      className={cn('size-3.5 shrink-0 transition-transform', !open && '-rotate-90')}
                    />
                    <span className="truncate">{g.label}</span>
                  </button>
                  <ul className={cn('space-y-0.5 pt-1', !open && 'hidden')}>
                    {items.map((s) => {
                      const isActive = s.key === active.key;
                      return (
                        <li key={s.key}>
                          <Link
                            to={'/settings/$section' as never}
                            params={{ section: s.key } as never}
                            className={cn(
                              'relative flex items-center gap-3 w-full px-3 h-10 rounded-lg text-[14px] transition-colors',
                              isActive
                                ? 'bg-surface text-ink font-medium'
                                : 'text-steel hover:bg-surface hover:text-ink',
                            )}
                          >
                            {isActive && (
                              <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-sm bg-brand-green" aria-hidden />
                            )}
                            <s.icon className={cn('size-[18px] shrink-0', isActive && 'text-brand-green-deep')} />
                            <span className="truncate">{s.label}</span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </nav>

          {/* Active section content */}
          <div className="min-w-0">
            <Section />
          </div>
        </div>
      </div>
    </>
  );
}
